import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Alert, Button, Modal, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCanArrowUp } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useRestoreBankMutation } from "../../../redux/banks/bankAPI";
import { updateOpenModalRestoreBank } from "../../../redux/banks/bankSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { LOBBYVISION2_PROTOCOL, logicalPortOutOfRange } from "../../../constants/bankLogicalPort";
// UTILS
import {
  clampLogicalPort,
  isBadRequest,
  notifyBankRestored,
  toRestoreBankResult,
} from "../../../utils/deletedBanks";
import { describeError } from "../../setupWizard/utils/describeError";
// COMPONENTS
import RestorePortField from "./RestorePortField";
// INTERFACES
import { RootState } from "../../../app/store";

/**
 * Confirms and performs the restore of the deleted bank chosen in
 * `BanksDeletedSection` (`bankToRestore` in the bank slice).
 *
 * The modal stays open when LiftCloud refuses, with the server's own message
 * shown inside it. A 400 means the bank's stored port is one its LobbyVision2
 * adapter cannot carry, so the modal then offers a port in range and repeats the
 * restore with it; the list does not say which adapter a bank used, so the
 * refusal is the signal rather than a check made up front.
 */
function BanksRestoreModal() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [errorMessage, setErrorMessage] = useState("");
  const [needsPort, setNeedsPort] = useState(false);
  const [port, setPort] = useState<number | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { bankToRestore, openModalRestoreBank } = useSelector((state: RootState) => state.bank);
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerRestoreBank, { isLoading }] = useRestoreBankMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleClose = () => {
    dispatch(updateOpenModalRestoreBank(false));
  };

  const handleRestore = async () => {
    if (bankToRestore === null) return;
    setErrorMessage("");
    try {
      const token = await GETJwtToken();
      const answer = await triggerRestoreBank({
        id: bankToRestore.id,
        body: needsPort && port !== null ? { logical_port: port } : undefined,
        token,
      }).unwrap();
      notifyBankRestored(
        t("edgeManagement.banks.deleted.restoredTitle", { name: bankToRestore.name }),
        toRestoreBankResult(answer),
        theme,
      );
      handleClose();
    } catch (err) {
      setErrorMessage(describeError(err));
      if (isBadRequest(err)) {
        setNeedsPort(true);
        setPort(
          (current) =>
            current ?? clampLogicalPort(LOBBYVISION2_PROTOCOL, bankToRestore.logicalPort),
        );
      }
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (openModalRestoreBank) {
      setErrorMessage("");
      setNeedsPort(false);
      setPort(null);
    }
  }, [openModalRestoreBank, bankToRestore]);

  const portOutOfRange =
    needsPort && (port === null || logicalPortOutOfRange(LOBBYVISION2_PROTOCOL, port));

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Modal
      closable={false}
      onCancel={handleClose}
      open={openModalRestoreBank}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        <div>
          <div className="text-center">
            <FontAwesomeIcon
              icon={faTrashCanArrowUp}
              className="generalStyles__info generalStyles__deleteItemIcon"
            />
            <p className="mt-3 drawer__text">
              {t("edgeManagement.banks.deleted.confirm", { name: bankToRestore?.name ?? "" })}
            </p>
            <p className="drawer__text">{t("edgeManagement.banks.deleted.limits")}</p>
          </div>
          {errorMessage !== "" && (
            <Alert type="error" showIcon message={errorMessage} style={{ marginBottom: 12 }} />
          )}
          {needsPort && (
            <RestorePortField
              protocolUpper={LOBBYVISION2_PROTOCOL}
              value={port}
              onChange={setPort}
            />
          )}
          <div className="generalStyles__flexEnd mt-4">
            <div>
              <Button
                type="primary"
                htmlType="submit"
                onClick={handleRestore}
                disabled={portOutOfRange}
              >
                {t("edgeManagement.banks.deleted.restore")}
              </Button>
              <Button onClick={handleClose} className="buttonStyle__4">
                {t("organizationManagement.close")}
              </Button>
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
}

export default BanksRestoreModal;
